import { unwrapEnvelopeOr } from '@/model/client';
import { useLazyGetApiProjectsByProjectIdQuery } from '@/model/project/api.generated';
import type { ProjectResponse } from '@/model/project/dto.generated';
import { logger } from '@/shared/helpers/logger';
import { useAppDispatch } from '@/shared/hooks/useStore';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import {
  clearCurrentProject,
  setCurrentProject,
} from '@/viewmodel/features/project/state/projectSlice';
import { setCurrentWorkspace, useWorkspaceQuery } from '@/viewmodel/features/workspace';
import { useCallback } from 'react';

interface ApplyContextScopeTarget {
  readonly workspaceId: string;
  readonly projectId?: string;
}

const UNREACHABLE_PROJECT_STATUSES = new Set([403, 404]);

export function useApplyContextScope(): (target: ApplyContextScopeTarget) => Promise<boolean> {
  const dispatch = useAppDispatch();
  const { workspaces } = useWorkspaceQuery();
  const [fetchProject] = useLazyGetApiProjectsByProjectIdQuery();

  return useCallback(
    async ({ workspaceId, projectId }: ApplyContextScopeTarget): Promise<boolean> => {
      const workspace = workspaces.find((w) => w.id === workspaceId);
      if (!workspace) return false;
      dispatch(setCurrentWorkspace(workspace));

      if (!projectId) {
        dispatch(clearCurrentProject());
        return true;
      }

      try {
        const result = await fetchProject({ projectId }, true).unwrap();
        const project = unwrapEnvelopeOr<ProjectResponse | undefined>(result, undefined);
        if (project?.workspace_id === workspaceId) dispatch(setCurrentProject(project));
        else dispatch(clearCurrentProject());
      } catch (error) {
        const status = getErrorStatus(error);
        if (status === undefined || !UNREACHABLE_PROJECT_STATUSES.has(status)) {
          logger.error('Failed to resolve context scope project', {
            log_source: 'useApplyContextScope',
            ...(status === undefined ? {} : { status }),
          });
        }
        dispatch(clearCurrentProject());
      }
      return true;
    },
    [workspaces, fetchProject, dispatch],
  );
}
